/**
 * Zentraler API-Client (architecture.md: „eine zentrale HTTP-Schicht").
 *
 * Verantwortung:
 *   - Envelope aus ADR 0001 auspacken; `data` zurueckgeben
 *   - CSRF-Token aus dem Cookie holen und als `X-CSRF-Token` mitschicken
 *   - Fehler zu einem `ApiError` normalisieren (Code, User-Meldung, Request-ID)
 *   - Session-Cookie mitsenden (`credentials: 'include'`)
 *
 * Komponenten fangen `ApiError` und zeigen `error.message` als Toast an —
 * technische Details stehen ausschliesslich im Server-Log.
 *
 * Die Pfade selbst stehen nicht hier, sondern in `lib/api-paths.ts` — von dort
 * liest sie auch der Contract-Test `packages/api/tests/ui-routes.test.ts`.
 */
import type { Envelope } from '@tickethub/shared';
import { API_BASE, apiPaths } from './api-paths';

const CSRF_COOKIE = 'tickethub_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const DEFAULT_TIMEOUT_MS = 30_000;
/** Uploads duerfen laenger dauern (frontend-ux.md). */
const UPLOAD_TIMEOUT_MS = 60_000;

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.code === 'UNAUTHORIZED';
  }

  get isRateLimited(): boolean {
    return this.code === 'RATE_LIMITED';
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/**
 * Holt das CSRF-Token. Der erste Aufruf setzt das Cookie serverseitig
 * (`GET /api/v1/csrf`), danach kommt es aus dem Cookie.
 */
export async function csrfToken(): Promise<string> {
  const cached = readCookie(CSRF_COOKIE);
  if (cached) return cached;

  const response = await fetch(`${API_BASE}${apiPaths.csrf}`, { credentials: 'include' });
  const envelope = (await response.json()) as Envelope<{ token: string }>;
  if (!envelope.success) throw toApiError(envelope, response.status);
  return envelope.data.token;
}

function toApiError(envelope: { error: { code: string; message: string; request_id?: string } }, status: number) {
  return new ApiError(
    envelope.error.code,
    envelope.error.message,
    status,
    envelope.error.request_id,
  );
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') headers[CSRF_HEADER] = await csrfToken();

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers,
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    // Netzwerk/Timeout — der Server hat nie geantwortet, es gibt keinen Envelope.
    throw new ApiError('SERVICE_UNAVAILABLE', 'network_error', 0);
  }

  const payload = (await response.json().catch(() => null)) as Envelope<T> | null;
  if (!payload) {
    throw new ApiError('INTERNAL_ERROR', 'invalid_response', response.status);
  }
  if (!payload.success) throw toApiError(payload, response.status);
  return payload.data;
}

/**
 * Datei-Upload (multipart). Eigener Weg, weil `request()` immer JSON schickt —
 * und weil Uploads laenger dauern duerfen (frontend-ux.md: 60 s).
 */
async function upload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { [CSRF_HEADER]: await csrfToken() },
      body: form,
      signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS),
    });
  } catch {
    throw new ApiError('SERVICE_UNAVAILABLE', 'network_error', 0);
  }

  const payload = (await response.json().catch(() => null)) as Envelope<T> | null;
  if (!payload) throw new ApiError('INTERNAL_ERROR', 'invalid_response', response.status);
  if (!payload.success) throw toApiError(payload, response.status);
  return payload.data;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path),
  upload,
};
